import { useMutation, InfiniteData, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { contentApi, IContentListResponse, IContent, ISearchedContent } from '@_entities/content';
import { toastGenerate, predicateFolder } from '@_shared/libs';

export function usePinContentMutation(content: IContent) {
	const queryClient = useQueryClient();

	// delete Contents Mutation
	const { mutate: pinContent } = useMutation({
		mutationFn: ({
			contentFixed,
			pinType,
			folderId,
		}: {
			contentFixed: boolean;
			pinType: 'folder' | 'entire';
			folderId: number;
		}) => {
			if (contentFixed) {
				return contentApi.unPinContent(
					content.id,
					pinType,
					pinType === 'folder' ? folderId : undefined,
				);
			}
			return contentApi.pinContent(
				content.id,
				pinType,
				pinType === 'folder' ? folderId : undefined,
			);
		},
		onMutate: async ({ contentFixed, pinType }) => {
			// 해당 요청으로 온, 남아있던 요청이 있다면 취소해서 충돌 방지
			await queryClient.cancelQueries({
				predicate: (query) =>
					pinType === 'entire'
						? predicateFolder(query, 'all')
						: predicateFolder(query, String(content.folderId)),
			});

			// 액션 전 데이터
			const previousContents = queryClient.getQueriesData<InfiniteData<IContentListResponse>>(
				{
					predicate: (query) =>
						pinType === 'entire'
							? predicateFolder(query, 'all')
							: predicateFolder(query, String(content.folderId)),
				},
			);

			// 액션 : 폴더내 컨텐츠 리스트 낙관적 업데이트
			queryClient.setQueriesData<InfiniteData<IContentListResponse>>(
				{
					predicate: (query) =>
						pinType === 'entire'
							? predicateFolder(query, 'all')
							: predicateFolder(query, String(content.folderId)),
				},
				(old) => {
					if (!old) return old;

					const fixTarget = old.pages
						.flatMap((page) => page.contentData)
						.find((pagedContent) => pagedContent.id === content.id) as ISearchedContent;

					if (contentFixed) {
						return {
							...old,
							pages: old.pages.map((page) => ({
								...page,
								pinnedData: page.pinnedData?.filter(
									(pinnedContent) => pinnedContent.id !== content.id,
								),
							})),
						};
					}

					return {
						...old,
						pages: old.pages.map((page, idx) => ({
							...page,
							contentData: page.contentData.filter(
								(pagedContent) => pagedContent.id !== content.id,
							),
							pinnedData:
								idx === 0
									? [{ ...fixTarget, isPinned: true }, ...(page.pinnedData || [])]
									: page.pinnedData,
						})),
					};
				},
			);

			return { previousContents };
		},
		onError: (error, { pinType }, context) => {
			if (error instanceof AxiosError) {
				if (error.status === 400) {
					if (pinType === 'entire') {
						toastGenerate({
							message:
								'전체 고정은 최대 5개까지 가능해요 :) 기존 고정 콘텐츠를 해제해주세요!',
						});
					} else {
						toastGenerate({
							message:
								'폴더별 고정은 최대 3개까지 가능해요 :) 기존 고정 콘텐츠를 해제해주세요!',
						});
					}
				}
			}
			context?.previousContents.forEach(([queryKey, previousData]) => {
				queryClient.setQueriesData<InfiniteData<IContentListResponse>>(
					{
						queryKey,
					},
					previousData,
				);
			});
		},
		onSettled: (_, __, params) => {
			queryClient.invalidateQueries({
				predicate: (query) =>
					params.pinType === 'entire'
						? predicateFolder(query, 'all')
						: predicateFolder(query, String(content.folderId)),
				refetchType: params.contentFixed ? 'active' : 'none',
			});
		},
	});

	return { pinContent };
}
